import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { createSite } from '@/api/sites';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

function Main() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const trimmedName = name.trim();
    const trimmedUrl = url.trim();

    if (!trimmedName) {
      setFormError('Name is required');
      return;
    }
    if (!trimmedUrl) {
      setFormError('URL is required');
      return;
    }

    setSubmitting(true);
    try {
      await createSite({
        name: trimmedName,
        url: trimmedUrl,
      });
      setFormSuccess('Site created successfully');
      setName('');
      setUrl('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <Link
          to="/manage-site"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-2"
        >
          <Lucide icon="ArrowLeft" className="w-4 h-4 mr-1.5" />
          Back to Manage Site
        </Link>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Add Site</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Create a new site URL
        </p>
      </div>

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm max-w-xl">
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              URL <span className="text-danger">*</span>
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={inputClass}
              placeholder="https://ldreamteam.my"
              required
            />
          </div>

          {formError && (
            <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
              {formError}
            </div>
          )}

          {formSuccess && (
            <div className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
              {formSuccess}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Lucide icon="Plus" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Add Site
                </>
              )}
            </Button>
            <Button type="button" variant="outline-primary" onClick={() => navigate('/manage-site')}>
              View list
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Main;
